import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { approveModelChange, fetchModelChanges, rejectModelChange } from "../services/modelling/modelStatusApi";
import type { ModelStatusApi } from "../services/modelling/modelStatus.types";
import { queryKeys } from "../services/queryKeys";

/** A held build's changes, read only while `runId` is set. */
export const useModelChanges = (runId: string | null) =>
  useQuery({
    queryKey: queryKeys.modelling.changes(runId ?? ""),
    queryFn: () => fetchModelChanges(runId as string),
    enabled: Boolean(runId),
    retry: false,
  });

/**
 * Approve and reject a held build. Approving shows a build under way at once (the status is read
 * again while it builds); rejecting reads the status again, since Modelling has recorded it by then.
 */
export const useModelReviewActions = () => {
  const queryClient = useQueryClient();
  const approve = useMutation({
    mutationFn: approveModelChange,
    onSuccess: () => {
      queryClient.setQueryData<ModelStatusApi>(queryKeys.modelling.status, (status) =>
        status
          ? { ...status, state: "building", started_at: new Date().toISOString(), reason: null, held_run_id: null }
          : status,
      );
    },
  });
  const reject = useMutation({
    mutationFn: rejectModelChange,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.modelling.status }),
  });
  return { approve, reject };
};
